import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ADMIN_BASE_PATH } from "../config/adminRoutes";
import { useAuth } from "../context/AuthContext"; // ← use YOUR AuthContext, not oidc directly

interface ProtectedRouteProps {
  children: React.ReactElement;
  redirectTo?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  redirectTo = "/",
}) => {
  const { isAuthenticated, loading, login } = useAuth();
  const location = useLocation();
  const [timedOut, setTimedOut] = useState(false);

  // Timeout after 3 seconds — don't block UI forever
  useEffect(() => {
    if (!loading) return;
    const timer = setTimeout(() => setTimedOut(true), 3000);
    return () => clearTimeout(timer);
  }, [loading]);

  // Still loading and not timed out — show spinner
  if (loading && !timedOut) {
    // Admin routes render immediately without spinner
    if (location.pathname.startsWith(ADMIN_BASE_PATH)) {
      return children;
    }

    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "50vh" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{
            width: "32px", height: "32px",
            border: "3px solid #f3f3f3",
            borderTop: "3px solid #fe2c55",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            margin: "0 auto 12px",
          }} />
          <p style={{ margin: 0, fontSize: "14px", color: "#666" }}>Loading...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  // Not authenticated — trigger OIDC login redirect instead of showing broken page
  if (!isAuthenticated) {
    // Trigger the Keycloak login flow automatically
    login();

    // Show a "redirecting" spinner while Keycloak redirect is in progress
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "50vh",
        flexDirection: "column" as const,
        gap: "1rem",
      }}>
        <div style={{
          width: "32px",
          height: "32px",
          border: "3px solid #f3f3f3",
          borderTop: "3px solid #fe2c55",
          borderRadius: "50%",
          animation: "spin 1s linear infinite",
        }} />
        <p style={{ margin: 0, fontSize: "14px", color: "#666" }}>
          Redirecting to sign in...
        </p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;